import { useEffect, useMemo, useState } from "react";
import {
  Download,
  ExternalLink,
  RefreshCw,
  Save,
  ShieldAlert,
} from "lucide-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Loading, PageHeader } from "../../components/shared";

const STATES = [
  "enabled",
  "disabled",
  "trial",
  "paid",
  "waived",
  "suspended",
  "expired",
];
const REQUEST_STATES = ["PENDING", "UNDER_REVIEW", "ACCEPTED", "DECLINED"];

function stateTone(state) {
  if (["enabled", "paid", "waived", "trial"].includes(state))
    return "bg-emerald-100 text-emerald-800";
  if (state === "disabled") return "bg-slate-100 text-slate-700";
  return "bg-amber-100 text-amber-800";
}

export default function AdminFeatures() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { societyId } = useParams();
  const navigate = useNavigate();
  const [matrix, setMatrix] = useState(null);
  const [requests, setRequests] = useState([]);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState({ q: "", featureKey: "", state: "" });
  const [saving, setSaving] = useState("");

  const load = async () => {
    try {
      const [matrixResponse, requestResponse] = await Promise.all([
        api.get("/admin/features/matrix", {
          params: {
            q: filters.q,
            feature_key: filters.featureKey,
            state: filters.state,
          },
        }),
        api.get("/admin/features/requests"),
      ]);
      setMatrix(matrixResponse.data);
      setRequests(requestResponse.data);
      if (societyId) {
        const detailResponse = await api.get(
          `/admin/features/societies/${societyId}`,
        );
        setDetail(detailResponse.data);
      } else {
        setDetail(null);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "Could not load Society feature controls",
      );
      setMatrix({ definitions: [], rows: [] });
    }
  };

  useEffect(() => {
    load();
  }, [societyId, filters.q, filters.featureKey, filters.state]);

  const definitions = matrix?.definitions || [];
  const featureCount = useMemo(
    () =>
      matrix?.rows.reduce((total, row) => total + row.features.length, 0) || 0,
    [matrix],
  );

  const migrate = async () => {
    try {
      const response = await api.post("/admin/features/migration/run");
      toast.success(
        `${response.data.entitlements_created} missing entitlement records added`,
      );
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Migration could not run");
    }
  };

  const exportMatrix = () => {
    window.open(
      `${API}/admin/features/export.csv`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const saveFeature = async (feature, state, reason, acknowledged) => {
    const key = `${feature.feature_key}-${state}`;
    setSaving(key);
    try {
      await api.put(
        `/admin/features/societies/${societyId}/${feature.feature_key}`,
        {
          state,
          reason,
          configuration: feature.configuration || {},
          acknowledge_financial_preservation: acknowledged,
        },
      );
      toast.success(`${feature.name} updated`);
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Feature could not be updated",
      );
    } finally {
      setSaving("");
    }
  };

  const updateRequest = async (requestId, status) => {
    try {
      await api.patch(`/admin/features/requests/${requestId}`, {
        status,
        review_note: "Reviewed in SA-007",
      });
      toast.success("Feature request updated");
      load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Request could not be updated",
      );
    }
  };

  if (!matrix) return <Loading />;

  if (societyId && detail) {
    return (
      <SocietyDetail
        detail={detail}
        onBack={() => navigate("/admin/features")}
        onSave={saveFeature}
        saving={saving}
      />
    );
  }

  return (
    <div data-testid="admin-features-page">
      <PageHeader
        title="Society Features & Add-ons"
        subtitle="Controlled platform entitlements. Society-local roles cannot grant feature access."
      >
        <Button
          data-testid="feature-migration-button"
          onClick={migrate}
          variant="outline"
        >
          <RefreshCw className="h-4 w-4" />
          Verify migration
        </Button>
        <Button
          data-testid="feature-export-button"
          onClick={exportMatrix}
          variant="outline"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </PageHeader>

      <section
        className="mb-7 grid gap-4 sm:grid-cols-3"
        data-testid="feature-summary"
      >
        <Summary
          label="Registered features"
          value={String(definitions.length)}
        />
        <Summary
          label="Societies in matrix"
          value={String(matrix.rows.length)}
        />
        <Summary
          label="Filtered feature records"
          value={String(featureCount)}
        />
      </section>

      <Tabs onValueChange={(v) => setSearchParams(v === "matrix" ? {} : { tab: v })} value={["matrix","registry","requests"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "matrix"}>
        <TabsList className="h-auto flex-wrap" data-testid="feature-tabs">
          <TabsTrigger data-testid="feature-matrix-tab" value="matrix">
            Feature matrix
          </TabsTrigger>
          <TabsTrigger data-testid="feature-registry-tab" value="registry">
            Registry
          </TabsTrigger>
          <TabsTrigger data-testid="feature-requests-tab" value="requests">
            Requests
          </TabsTrigger>
        </TabsList>
        <TabsContent value="matrix">
          <section
            className="mt-5 flex flex-wrap gap-3"
            data-testid="feature-matrix-filters"
          >
            <Input
              className="max-w-xs"
              data-testid="feature-search-input"
              onChange={(event) =>
                setFilters((current) => ({ ...current, q: event.target.value }))
              }
              placeholder="Search Society or city"
              value={filters.q}
            />
            <select
              className="form-select w-52"
              data-testid="feature-key-filter"
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  featureKey: event.target.value,
                }))
              }
              value={filters.featureKey}
            >
              <option value="">All features</option>
              {definitions.map((feature) => (
                <option key={feature.feature_key} value={feature.feature_key}>
                  {feature.feature_code} · {feature.name}
                </option>
              ))}
            </select>
            <select
              className="form-select w-44"
              data-testid="feature-state-filter"
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  state: event.target.value,
                }))
              }
              value={filters.state}
            >
              <option value="">All states</option>
              {STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </section>
          <section
            className="mt-5 overflow-x-auto border bg-card"
            data-testid="feature-matrix-table"
          >
            <table className="min-w-[760px] w-full text-left text-sm">
              <thead className="border-b bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th className="p-3">Society</th>
                  <th className="p-3">Feature states</th>
                  <th className="p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {matrix.rows.map((row) => (
                  <tr
                    data-testid={`feature-matrix-row-${row.society_id}`}
                    className="border-b"
                    key={row.society_id}
                  >
                    <td className="p-3 font-medium">
                      <div>{row.society_name}</div>
                      <div className="mt-1 text-xs font-normal text-muted-foreground">
                        {row.city || "No city"}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex max-w-3xl flex-wrap gap-2">
                        {row.features.map((feature) => (
                          <button
                            className={`rounded-full px-2.5 py-1 text-xs ${stateTone(feature.state)}`}
                            data-testid={`feature-matrix-status-${row.society_id}-${feature.feature_key}`}
                            key={feature.feature_key}
                            onClick={() =>
                              navigate(`/admin/features/${row.society_id}`)
                            }
                            type="button"
                          >
                            {feature.feature_code}: {feature.state}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="p-3">
                      <Link
                        data-testid={`feature-matrix-open-${row.society_id}`}
                        to={`/admin/features/${row.society_id}`}
                      >
                        <Button size="sm" variant="outline">
                          Manage <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </TabsContent>
        <TabsContent value="registry">
          <Registry definitions={definitions} />
        </TabsContent>
        <TabsContent value="requests">
          <Requests onUpdate={updateRequest} requests={requests} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SocietyDetail({ detail, onBack, onSave, saving }) {
  return (
    <div data-testid="admin-society-feature-detail">
      <PageHeader
        title={detail.society.name || "Society features"}
        subtitle="Per-Society control with immutable history and dependency safeguards."
      >
        <Button
          data-testid="feature-detail-back-button"
          onClick={onBack}
          variant="outline"
        >
          Back to matrix
        </Button>
      </PageHeader>
      <section
        className="grid gap-4 lg:grid-cols-2"
        data-testid="feature-detail-list"
      >
        {detail.features.map((feature) => (
          <FeatureControl
            feature={feature}
            key={feature.feature_key}
            onSave={onSave}
            saving={saving}
          />
        ))}
      </section>
      <section
        className="mt-8 border bg-card p-5"
        data-testid="feature-history-section"
      >
        <h2 className="font-display text-xl font-bold">Entitlement history</h2>
        <div className="mt-4 space-y-3">
          {detail.history.length === 0 ? (
            <p
              className="text-sm text-muted-foreground"
              data-testid="feature-history-empty"
            >
              No controlled changes recorded.
            </p>
          ) : (
            detail.history.map((entry) => (
              <article
                className="border-l-2 border-primary/40 pl-3 text-sm"
                data-testid={`feature-history-${entry.history_id}`}
                key={entry.history_id}
              >
                <div className="font-medium">
                  {entry.feature_key}: {entry.from_state || "unassigned"} →{" "}
                  {entry.to_state}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {entry.reason} · {String(entry.changed_at || "").slice(0, 19)}
                </div>
              </article>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

function FeatureControl({ feature, onSave, saving }) {
  const [state, setState] = useState(feature.state);
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const requiresWarning =
    feature.financial &&
    !["enabled", "trial", "paid", "waived"].includes(state);
  const busy = saving === `${feature.feature_key}-${state}`;

  useEffect(() => {
    setState(feature.state);
  }, [feature.state]);

  return (
    <article
      className="border bg-card p-5"
      data-testid={`feature-control-${feature.feature_key}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold text-primary">
            {feature.feature_code}
          </div>
          <h2 className="font-display text-xl font-bold">{feature.name}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {feature.description}
          </p>
        </div>
        <span
          className={`rounded-full px-2.5 py-1 text-xs ${stateTone(feature.state)}`}
          data-testid={`feature-current-state-${feature.feature_key}`}
        >
          {feature.state}
        </span>
      </div>
      <div className="mt-5 grid gap-3">
        <label
          className="text-sm font-medium"
          htmlFor={`feature-state-${feature.feature_key}`}
        >
          Access state
        </label>
        <select
          className="form-select"
          data-testid={`feature-state-select-${feature.feature_key}`}
          id={`feature-state-${feature.feature_key}`}
          onChange={(event) => setState(event.target.value)}
          value={state}
        >
          {STATES.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
        <Input
          data-testid={`feature-reason-${feature.feature_key}`}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Reason for this controlled change"
          value={reason}
        />
        {feature.dependencies.length > 0 && (
          <p
            className="text-xs text-muted-foreground"
            data-testid={`feature-dependencies-${feature.feature_key}`}
          >
            Depends on: {feature.dependencies.join(", ")}
          </p>
        )}
        {requiresWarning && (
          <label
            className="flex gap-2 border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-950"
            data-testid={`feature-financial-warning-${feature.feature_key}`}
          >
            <input
              checked={acknowledged}
              data-testid={`feature-financial-acknowledgement-${feature.feature_key}`}
              onChange={(event) => setAcknowledged(event.target.checked)}
              type="checkbox"
            />{" "}
            <span>
              <ShieldAlert className="mr-1 inline h-4 w-4" />I confirm access
              will be blocked while all financial records, history, balances,
              reports, and audits remain preserved.
            </span>
          </label>
        )}
        <Button
          data-testid={`feature-save-${feature.feature_key}`}
          disabled={
            !reason.trim() || (requiresWarning && !acknowledged) || busy
          }
          onClick={() => onSave(feature, state, reason, acknowledged)}
        >
          {busy ? (
            "Saving..."
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save entitlement
            </>
          )}
        </Button>
      </div>
    </article>
  );
}

function Registry({ definitions }) {
  return (
    <section
      className="mt-5 grid gap-4 md:grid-cols-2"
      data-testid="feature-registry-list"
    >
      {definitions.map((feature) => (
        <article
          className="border bg-card p-5"
          data-testid={`feature-registry-${feature.feature_key}`}
          key={feature.feature_key}
        >
          <div className="text-xs font-semibold text-primary">
            {feature.feature_code}
          </div>
          <h2 className="font-display text-xl font-bold">{feature.name}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {feature.description}
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <span className="border px-2 py-1">{feature.category}</span>
            <span className="border px-2 py-1">{feature.availability}</span>
            {feature.financial && (
              <span className="border border-amber-400 bg-amber-50 px-2 py-1 text-amber-800">
                Financial preservation guarded
              </span>
            )}
          </div>
        </article>
      ))}
    </section>
  );
}

function Requests({ onUpdate, requests }) {
  return (
    <section className="mt-5 space-y-3" data-testid="feature-request-list">
      {requests.length === 0 ? (
        <p
          className="border border-dashed p-8 text-center text-sm text-muted-foreground"
          data-testid="feature-request-empty"
        >
          No feature requests recorded.
        </p>
      ) : (
        requests.map((request) => (
          <article
            className="border bg-card p-5"
            data-testid={`feature-request-${request.request_id}`}
            key={request.request_id}
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-lg font-bold">
                  {request.title}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {request.society_name} · {request.description}
                </p>
              </div>
              <select
                className="form-select w-44"
                data-testid={`feature-request-state-${request.request_id}`}
                onChange={(event) =>
                  onUpdate(request.request_id, event.target.value)
                }
                value={request.status}
              >
                {REQUEST_STATES.map((state) => (
                  <option key={state}>{state}</option>
                ))}
              </select>
            </div>
          </article>
        ))
      )}
    </section>
  );
}

function Summary({ label, value }) {
  return (
    <article
      className="border bg-card p-5"
      data-testid={`feature-summary-${label.toLowerCase().replaceAll(" ", "-")}`}
    >
      <div className="font-display text-3xl font-bold">{value}</div>
      <div className="mt-1 text-sm text-muted-foreground">{label}</div>
    </article>
  );
}
